import React from 'react'

import { datetime, timeAgo, getNameOrEmailSlug } from 'utils'

import type { ScanLog } from 'types'

import { Stack, Typography, Divider, Tooltip, Box } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ShieldAlert, ShieldCheck, ShieldOff, Link2Off, ClockAlert, Unlink, UserRound, LockOpen, Nfc } from 'lucide-react'

import { StatusChip, UserBlock } from 'components'
// Canonical resolver (VERA-476): the local `hooks/api` copy of isRenderResolve /
// renderResolveMeta predates this and lacks `description` — read from `@repo/ui`
// here instead of adding a second, drifting copy of that field (§36). Identical
// key/label/color logic, so this is a type-only convergence, not a behavior change.
import { getScanStatus, isRenderResolve, renderResolveMeta, type ScanStatusIcon } from '@repo/ui'

/**
 * Icon-name → component, mirroring `apps/app`'s `ScanStatusChip` (VRG-155 / TD-061):
 * the shared resolver names the glyph but can't import one (it's a framework-free
 * module shared by two apps), so the mapping lives per-app.
 *
 * Exported so the "Learn more about scan states" explainer modal (VERA-476, see
 * `Scans.tsx`) reuses this SAME mapping instead of a second hand-rolled copy (§36).
 */
export const ICONS: Record<ScanStatusIcon, React.ElementType> = {
  'shield-alert': ShieldAlert,
  // VERA-260: the confirmed-genuine mark. No list row draws it (verified is
  // `showChip: false`); the analytics map legend does, because a legend must name
  // every outcome it keys.
  'shield-check': ShieldCheck,
  'shield-off': ShieldOff,
  'link-2-off': Link2Off,
  'clock-alert': ClockAlert,
  // VERA-628: the seal-opened state — a status, not a breach.
  'lock-open': LockOpen,
  // VRG-252: a Connect tap. The NFC mark names the EVENT rather than grading it — the same
  // reasoning that put this glyph on the consumer app's authenticity badge.
  nfc: Nfc,
}

// VRG-164: brand-tinted "Owner" badge for a scan attributed to the certificate owner.
const OWNER_STATUS = {
  owner: { label: 'Owner', color: '#7C3AED', icon: <UserRound size={13} strokeWidth={2} /> },
}

interface ScanItemProps {
  scan: ScanLog
  onClick?: (id: string) => void
  isActive?: boolean
}

export const ScanItem = ({ scan, onClick, isActive }: ScanItemProps) => {
  const meta = scan.metadata as any
  // VRG-161: an anonymous scan has no linked user, but if the scanner submitted an
  // email on the public cert page it's merged onto this scan's metadata — surface it
  // as the identity instead of "Anonymous". Falls back through user → collected email.
  const fullName =
    (getNameOrEmailSlug(scan.user) as string) || meta?.email || 'Anonymous'
  // VRG-164: this scan was attributed to the certificate owner (they tapped while logged
  // in) — show an "Owner" badge alongside their name.
  const isOwnerScan = !!meta?.attributed_owner
  // Known scanner (a resolved user — e.g. the collected email matched an existing
  // account like sjfstebbins@gmail.com) → render their photo/initials + name via
  // UserBlock. Otherwise it's anonymous (or an email-only lead with no matching user):
  // pass just the collected email (or nothing) so UserBlock shows the generic person
  // icon — NOT an "A" initial — with the email/"Anonymous" as the label.
  const hasIdentity = !!(
    scan.user &&
    ((scan.user as any).name ||
      (scan.user as any).first_name ||
      (scan.user as any).last_name)
  )
  const userForBlock = hasIdentity
    ? scan.user
    : { email: (scan.user as any)?.email || meta?.email || undefined }
  // VRG-165: a submitted location is stored NESTED as metadata.location = { latitude,
  // longitude, accuracy } — so read those too, not just the top-level meta.lat/latitude
  // (which anonymous geo-IP scans use). Never render the location object directly (#31).
  const loc = meta?.location && typeof meta.location === 'object' ? meta.location : null
  const lat = meta?.lat ?? meta?.latitude ?? loc?.latitude
  const lng = meta?.long ?? meta?.longitude ?? loc?.longitude
  // No location recorded → leave the second line blank (don't claim "hidden").
  // VRG-165: prefer the geocoded "City, State, Country" (only the parts we have), then a
  // string location, then raw lat/lng as a last resort.
  const geoPlace = [meta?.city, meta?.state, meta?.country]
    .filter((p) => typeof p === 'string' && p.trim())
    .join(', ')
  const locationText =
    geoPlace ||
    (typeof meta?.location === 'string' ? meta.location : '') ||
    (lat != null ? `${lat}, ${lng}` : '')

  // VRG-106: render-token (`vt`) verification-failure EVENTS (a stale/cloned/forged
  // certificate link being opened) are a distinct kind from physical scans — never a
  // tap — so they get their own StatusChip status + Unlink icon, entirely separate
  // from the valid/invalid/tampered physical-scan path below.
  const renderResolve = isRenderResolve(scan as any)
  const renderMeta = renderResolve ? renderResolveMeta((scan as any).render_reason) : null
  const renderStatuses = renderMeta
    ? {
        [renderMeta.key]: {
          label: renderMeta.label,
          color: renderMeta.color,
          icon: <Unlink size={13} strokeWidth={2} />,
          // The resolver already names which entry defines this state, so the chip carries
          // it and `StatusChip` does the rest (VRG-437). This used to be an `InfoTooltip`
          // wrapped around the chip HERE — same tooltip, but the wrapper was a bare <span>,
          // so the chip inherited this row's `cursor: pointer` and read as a link to
          // somewhere, and a tap opened the scan instead of the definition.
          glossaryId: renderMeta.glossaryId,
        },
      }
    : null
  const renderLocationText = (scan as any).geo_country || ''

  // Physical-scan status (tampered / invalid / verified), resolved by the shared
  // `getScanStatus` (VRG-155 / TD-061) so this row's chip, the map pin, and the map
  // tooltip can never drift apart — as of VRG-491 all three genuinely read that resolver.
  // The tail of this note used to say "Invalid and Tampered are both error-red now, so the
  // icon carries the distinction the colour used to." That is no longer true and was the
  // claim the reframe removed: only Invalid is red. Tampered resolves to a neutral zinc
  // "Opened" with an open padlock, because the tag reports a seal circuit opening and
  // cannot report who opened it or why.
  const physicalStatus = getScanStatus(scan as any)
  const PhysicalIcon = physicalStatus.icon ? ICONS[physicalStatus.icon] : undefined
  const physicalStatuses = {
    [physicalStatus.key]: {
      label: physicalStatus.label,
      color: physicalStatus.color,
      icon: PhysicalIcon ? <PhysicalIcon size={13} strokeWidth={2} /> : undefined,
      glossaryId: physicalStatus.glossaryId,
    },
  }

  return (
    <>
      <Stack
        data-scan-id={scan.id}
        direction="row"
        spacing={4}
        py={3}
        px={4}
        justifyContent="space-between"
        alignItems="center"
        onClick={() => onClick?.(scan.id)}
        sx={{
          cursor: 'pointer',
          transition: 'background-color 0.15s, box-shadow 0.15s',
          // Persistent selected state — brand tint + inset left accent — so the row
          // reflects the same selection the map dot (pulse + tooltip) does.
          backgroundColor: isActive
            ? (t) => alpha(t.palette.brand?.[500] ?? t.palette.primary.main, 0.14)
            : 'transparent',
          boxShadow: isActive
            ? (t) => `inset 3px 0 0 ${t.palette.brand?.[500] ?? t.palette.primary.main}`
            : 'none',
          '&:hover': {
            backgroundColor: (t) =>
              isActive
                ? alpha(t.palette.brand?.[500] ?? t.palette.primary.main, 0.14)
                : t.palette.action.hover,
          },
        }}
      >
        {/* Reuse the shared UserBlock (avatar + name + secondary) so the scanner cell
            matches the Users table 1:1 — same avatar, same body3/body4 sizes — with the
            location as the second line instead of the email. */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <UserBlock
            user={userForBlock}
            secondary={renderResolve ? renderLocationText : locationText}
            size={44}
          />
        </Box>

        {/* Badge to the LEFT of the date on ONE line — so the date stays vertically
            centered and consistent whether or not a badge is present (a stacked badge
            pushed the date up on flagged rows). Valid is unbadged (expected state). */}
        <Stack direction="row" spacing={3} alignItems="center" flexShrink={0}>
          {isOwnerScan && (
            <StatusChip statuses={OWNER_STATUS} status="owner" size="small" />
          )}
          {/* The badge itself explains the state (§44): the definition comes from
              `docs/glossary.json`, the resolver names WHICH entry via `glossaryId`, and the
              tooltip's footer deep links to that term's anchor. This replaces the "Learn
              more about scan states" modal that used to carry the six definitions from a
              detached link at the bottom of the tab: the question is raised by the badge,
              so the answer belongs on the badge.

              The wiring now lives in `StatusChip` rather than here (VRG-437) — the
              `glossaryId` on each status above is the whole call site. Worth doing even
              though the hand-rolled version already showed the tooltip, because a shared
              trigger is what gives the chip `cursor: help` instead of this row's inherited
              `pointer`, a tab stop, and a tap that reveals rather than opening the scan. */}
          {renderResolve && renderMeta && renderStatuses ? (
            <StatusChip statuses={renderStatuses} status={renderMeta.key} size="small" />
          ) : (
            physicalStatus.showChip && (
              <StatusChip statuses={physicalStatuses} status={physicalStatus.key} size="small" />
            )
          )}
          <Tooltip title={datetime(scan.created_at)} placement="left" arrow>
            <Typography variant="body3" color="text.secondary" noWrap sx={{ cursor: 'default' }}>
              {timeAgo(scan.created_at)}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
      <Divider />
    </>
  )
}
